import React from 'react';
import {
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerHeader,
  DrawerOverlay,
  Flex,
  Spinner,
  Text,
} from '@chakra-ui/react';
import { useQuery } from '@tanstack/react-query';
import { useAuditFidelidade } from '../../../../hooks/useAuditFidelidade';
import { RelatorioFidelidade } from './RelatorioFidelidade';

interface Props {
  contratoId: number | null;
  // Fase 1 gate: id do relatório já confirmado no contrato (auditoria_revisada_id).
  revisadaId?: number | null;
  isOpen: boolean;
  onClose: () => void;
  // Chamado após confirmar a vistoria pela lupa (invalida a lista → atualiza o card).
  onRevisado?: () => void;
}

// Drawer lateral com a vistoria da IA daquele contrato (comparativo vigente x render).
export const RelatorioFidelidadeDrawer = ({
  contratoId,
  revisadaId,
  isOpen,
  onClose,
  onRevisado,
}: Props) => {
  const { getAuditoriaContrato } = useAuditFidelidade();
  const { data, isFetching, isError } = useQuery({
    queryKey: ['auditoria-fidelidade-contrato', contratoId],
    queryFn: () => getAuditoriaContrato(contratoId as number),
    enabled: isOpen && contratoId != null,
    staleTime: 60 * 1000,
  });

  const renderConteudo = () => {
    if (isFetching) {
      return (
        <Flex justify="center" py={10}>
          <Spinner color="#7C3AED" thickness="3px" />
        </Flex>
      );
    }
    if (isError) {
      return (
        <Text color="red.500" fontSize="sm">
          Falha ao carregar — acesso restrito a admin/dev.
        </Text>
      );
    }
    if (!data) {
      return (
        <Text color="#71717A" fontSize="sm">
          Sem relatório de fidelidade para este contrato. Gerado antes do auditor, ou geração
          v2/constituição — o auditor só roda em alteração v3.
        </Text>
      );
    }
    return (
      <RelatorioFidelidade
        auditoria={data}
        contratoId={contratoId ?? undefined}
        revisada={revisadaId != null && revisadaId === data.id}
        onRevisado={onRevisado}
      />
    );
  };

  return (
    <Drawer isOpen={isOpen} onClose={onClose} size="xl" placement="right">
      <DrawerOverlay />
      <DrawerContent>
        <DrawerCloseButton />
        <DrawerHeader borderBottomWidth="1px">
          Relatório de Fidelidade{contratoId ? ` — contrato ${contratoId}` : ''}
        </DrawerHeader>
        <DrawerBody py={5}>{renderConteudo()}</DrawerBody>
      </DrawerContent>
    </Drawer>
  );
};

export default RelatorioFidelidadeDrawer;
